import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../config/firebase";
import { collection, query, getDocs, doc, updateDoc, deleteDoc, orderBy, limit, where, addDoc } from "firebase/firestore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Loader2,
  Pencil,
  XCircle,
  Trash2,
  Save,
  MoreHorizontal
} from "lucide-react";
import {
  AnalyticsLineIcon,
  VolumeBarIcon,
  ArrowUpIcon,
  ChartGridIcon,
  CandlestickIcon
} from "../components/icons/TradingIcons";
import TradingBackground from "../components/theme/TradingBackground";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { format } from "date-fns";
import { toast } from "react-toastify";

export default function AdminPanel() {
  const { user: currentUser, loading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ question: "", answer: "", category: "" });
  const queryClient = useQueryClient();

  const isAdmin = currentUser?.role === "admin";
  console.log("AdminPanel Check:", { uid: currentUser?.uid, email: currentUser?.email, role: currentUser?.role, isAdmin });

  // --- QUERIES ---

  // 1. Fetch Users (From Firestore 'users' collection)
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      try {
        const usersRef = collection(db, "users");
        // Warning: Fetching all users might be heavy in prod. For now it's okay.
        const snapshot = await getDocs(usersRef);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (err) {
        console.error("Failed to fetch users from Firestore:", err);
        return [];
      }
    },
    enabled: isAdmin
  });

  // 2. Fetch Knowledge Base (From Firestore 'knowledge_base' collection)
  const { data: kbEntries = [], isLoading: loadingKB, error: kbError } = useQuery({
    queryKey: ["admin-kb"],
    queryFn: async () => {
      try {
        const kbRef = collection(db, "knowledge_base");
        const q = query(kbRef, orderBy("created_at", "desc"));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (err) {
        console.error("Failed to fetch KB from Firestore:", err);
        throw err; // Throw error to be caught by useQuery
      }
    },
    enabled: isAdmin
  });

  // 3. Fetch Messages (From Firestore 'admin_support' collection)
  const { data: messages = [], isLoading: loadingMessages } = useQuery({
    queryKey: ["admin-messages"],
    queryFn: async () => {
      try {
        const msgRef = collection(db, "admin_support");
        const q = query(msgRef, orderBy("created_at", "desc"), limit(50));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (err) {
        console.error("Failed to fetch messages from Firestore:", err);
        return [];
      }
    },
    enabled: isAdmin
  });

  // --- MUTATIONS ---

  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role }) => {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { role: role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User role updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update user role:", error);
      toast.error("Failed to update user role");
    }
  });

  const updateKBEntryMutation = useMutation({
    mutationFn: async ({ entryId, data }) => {
      const kbRef = doc(db, "knowledge_base", entryId);
      await updateDoc(kbRef, { ...data, updated_at: new Date() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-kb"] });
      setEditingEntry(null);
      toast.success("KB entry updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update KB entry:", error);
      toast.error("Failed to update entry");
    }
  });

  const deleteKBEntryMutation = useMutation({
    mutationFn: async (entryId) => {
      const kbRef = doc(db, "knowledge_base", entryId);
      await deleteDoc(kbRef);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-kb"] });
      toast.success("KB entry deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete KB entry:", error);
      toast.error("Failed to delete entry");
    }
  });

  // --- RENDER ---

  if (authLoading) {
    return (
      <div className="min-h-screen items-center justify-center flex bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen trading-bg flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8 trading-card">
          <AnalyticsLineIcon className="w-16 h-16 trading-icon-muted mx-auto mb-4" />
          <h2 className="text-xl font-bold trading-text mb-2">Access Denied</h2>
          <p className="trading-text-muted">You need admin privileges to access this page.</p>
          <div className="mt-4 p-2 bg-gray-100 rounded text-xs text-left">
            <p>Debug Info:</p>
            <p>User: {currentUser?.email || 'Unknown'}</p>
            <p>Role: {currentUser?.role || 'None'}</p>
          </div>
        </Card>
      </div>
    );
  }

  const filteredUsers = users.filter(u =>
    (u.fullName || u.firstName || u.email || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    totalUsers: users.length,
    verifiedContributors: users.filter(u => u.role === "verified").length,
    admins: users.filter(u => u.role === "admin").length,
    kbArticles: kbEntries.length,
    messages: messages.length
  };

  // Predefined categories list
  const PREDEFINED_CATEGORIES = ["indicators", "alerts", "tradingview", "mt4_mt5", "strategy", "risk_management", "troubleshooting"];

  // State for adding new entry
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [newEntryForm, setNewEntryForm] = useState({ title: "", content: "", category: "troubleshooting", customCategory: "" });

  const seedKBData = async () => {
    if (!confirm("Are you sure you want to add default KB data? This will add several new entries.")) return;

    const initialData = [
      {
        title: "Introduction to Moving Averages",
        content: "Moving averages smooth out price data to form a trend following indicator. They do not predict price direction, but rather define the current direction with a lag. Moving averages lag because they are based on past prices.",
        category: "indicators",
        videoUrl: ""
      },
      {
        title: "Setting up Price Alerts",
        content: "To set up a price alert: 1. Open the chart. 2. Right-click at the price level you want. 3. Select 'Add Alert'. 4. Configure your notification settings (email, app, or popup).",
        category: "alerts",
        videoUrl: ""
      },
      {
        title: "Basic Risk Management Rule",
        content: "The 1% Rule: Never risk more than 1% of your account on a single trade. If you have a $10,000 account, your risk per trade should not exceed $100. This ensures you can survive a losing streak.",
        category: "risk_management",
        videoUrl: ""
      },
      {
        title: "How to use TradingView",
        content: "TradingView is a powerful charting platform. To get started: 1. Create an account. 2. Search for a symbol (e.g., EURUSD). 3. Use the toolbar on the left for drawing tools. 4. Use the top bar to change timeframes and add indicators.",
        category: "tradingview",
        videoUrl: ""
      }
    ];

    try {
      const kbRef = collection(db, "knowledge_base");
      for (const item of initialData) {
        await addDoc(kbRef, {
          ...item,
          views: 0,
          contributor_name: "System Admin",
          created_at: new Date(),
          updated_at: new Date()
        });
      }
      queryClient.invalidateQueries({ queryKey: ["admin-kb"] });
      toast.success("Default data formulated successfully!");
    } catch (error) {
      console.error("Error seeding data:", error);
      toast.error("Failed to seed data");
    }
  };

  const addKBEntryMutation = useMutation({
    mutationFn: async (data) => {
      const kbRef = collection(db, "knowledge_base");
      // Determine final category
      const finalCategory = data.category === "other" ? data.customCategory : data.category;

      await addDoc(kbRef, {
        title: data.title,
        content: data.content,
        category: finalCategory,
        views: 0,
        contributor_name: currentUser?.firstName || "Admin",
        created_at: new Date(),
        updated_at: new Date()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-kb"] });
      setIsAddingEntry(false);
      setNewEntryForm({ title: "", content: "", category: "troubleshooting", customCategory: "" });
      toast.success("New KB entry added successfully");
    },
    onError: (error) => {
      console.error("Failed to add KB entry:", error);
      toast.error("Failed to add new entry");
    }
  });

  const handleAddEntry = () => {
    if (!newEntryForm.title || !newEntryForm.content) {
      toast.error("Please fill in both title and content");
      return;
    }
    if (newEntryForm.category === "other" && !newEntryForm.customCategory) {
      toast.error("Please specify the custom category");
      return;
    }
    addKBEntryMutation.mutate(newEntryForm);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    const isPredefined = PREDEFINED_CATEGORIES.includes(entry.category);
    setEditForm({
      title: entry.title || entry.question, // Fallback for old data
      content: entry.content || entry.answer, // Fallback for old data
      category: isPredefined ? entry.category : "other",
      customCategory: isPredefined ? "" : entry.category
    });
  };

  const handleSaveEntry = () => {
    if (editForm.category === "other" && !editForm.customCategory) {
      toast.error("Please specify the custom category");
      return;
    }

    // Determine final category
    const finalCategory = editForm.category === "other" ? editForm.customCategory : editForm.category;

    updateKBEntryMutation.mutate({
      entryId: editingEntry.id,
      data: {
        title: editForm.title,
        content: editForm.content,
        category: finalCategory
      }
    });
  };

  // Fallback dummy data for design verification if Firestore permissions fail
  const dummyKBEntries = [
    { id: '1', category: 'alerts', title: 'How do I set up RSI alerts on TradingView?', content: "For RSI alerts: 1. Add RSI indicator to your chart 2. Click on the RSI line where you want the alert 3. Select 'Add Alert' 4. Set condition (e.g., 'Crossing Up' at 30 for oversold) 5. Choose notification method Pro tip: Use 'Once Per Bar Close' to avoid false signals during volatile moments!", contributor_name: 'Sarah Chen', views: 47 },
    { id: '2', category: 'risk_management', title: 'What is the recommended risk per trade for swing trading?', content: 'The general rule is 1-2% of your total account per trade. Here\'s why: • 1% risk = can handle 10+ consecutive losses • 2% risk = more aggressive but still sustainable • Never exceed 5% total risk across all open positions. For swing trading specifically, I recommend starting at 1% until you have a proven track record.', contributor_name: 'David Park', views: 123 },
    { id: '3', category: 'mt4_mt5', title: 'How do I install custom indicators on MT4?', content: 'To install custom indicators on MT4: 1. Download the .mq4 or .ex4 indicator file 2. Open MT4 and go to File > Open Data Folder 3. Navigate to MQL4 > Indicators folder 4. Copy your indicator file into this folder 5. Restart MT4 or right-click Navigator panel > Refresh 6. Find your indicator in Navigator > Indicators 7. Drag it onto your chart.', contributor_name: 'Sarah Chen', views: 89 }
  ];

  const displayEntries = kbEntries.length > 0 ? kbEntries : (loadingKB ? [] : dummyKBEntries);

  // --- SEED DATA HELPERS ---
  const seedUserData = async () => {
    if (!confirm("Are you sure you want to add demo users?")) return;
    try {
      const demoUsers = [
        { email: 'student1@example.com', displayName: 'Sarah Student', role: 'user', joined_date: new Date().toISOString() },
        { email: 'trader_pro@example.com', displayName: 'Pro Trader', role: 'user', joined_date: new Date().toISOString() },
        { email: 'newbie@example.com', displayName: 'Newbie John', role: 'user', joined_date: new Date().toISOString() }
      ];

      for (const u of demoUsers) {
        await addDoc(collection(db, "users"), u);
      }
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Demo users added successfully!");
    } catch (err) {
      console.error("Failed to seed users:", err);
      toast.error("Failed to seed users");
    }
  };

  const banUserMutation = useMutation({
    mutationFn: async ({ userId, isBanned }) => {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { isBanned: !isBanned });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User status updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update user ban status:", error);
      toast.error("Failed to update user status");
    }
  });

  // --- RENDER ---

  if (authLoading) {
    return (
      <div className="min-h-screen items-center justify-center flex bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  // --- SUB-COMPONENTS ---
  const UserManagementContent = () => {
    const displayUsers = users.filter(u =>
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <Card className="trading-card shadow-sm border border-gray-200 bg-white">
        <CardHeader className="bg-gray-50/50 border-b border-gray-100 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold text-gray-900">Users ({users.length})</CardTitle>
            <p className="text-sm text-gray-500 mt-1">Manage registered members</p>
          </div>
          {users.length === 0 && (
            <Button onClick={seedUserData} variant="outline" size="sm" className="bg-white border-dashed border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-400 hover:bg-white shadow-sm">
              + Seed Demo Users
            </Button>
          )}
        </CardHeader>
        <CardContent className="pt-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-50 border-gray-200 focus:bg-white transition-all text-gray-900"
            />
          </div>

          <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
            <Table>
              <TableHeader className="bg-gray-50/80">
                <TableRow>
                  <TableHead className="text-gray-900 font-semibold w-[250px]">User</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Email</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Joined</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Role</TableHead>
                  <TableHead className="text-right text-gray-900 font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingUsers ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-32 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                    </TableCell>
                  </TableRow>
                ) : displayUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-40 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                          <Search className="w-6 h-6 text-gray-300" />
                        </div>
                        <p>No users found matching your search.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayUsers.map((user) => (
                    <TableRow key={user.id} className={`hover:bg-gray-50 transition-colors ${user.isBanned ? 'bg-red-50/50 hover:bg-red-50' : ''}`}>
                      <TableCell className="font-medium text-gray-900">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm ${user.isBanned ? 'bg-gray-400' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
                            {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className={`font-semibold ${user.isBanned ? 'text-gray-500 line-through' : ''}`}>{user.displayName || "Unknown User"}</span>
                            {user.isBanned && <span className="ml-2 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold uppercase">BANNED</span>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">{user.email}</TableCell>
                      <TableCell className="text-gray-500 text-xs">
                        {user.joined_date ? new Date(user.joined_date).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={user.role === 'admin' ? 'default' : 'secondary'}
                          className={user.role === 'admin' ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200'}
                        >
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-900">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border-gray-200 shadow-xl z-50">
                            <DropdownMenuLabel>User Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => updateUserRoleMutation.mutate({ userId: user.id, role: user.role === 'admin' ? 'user' : 'admin' })}
                              className="cursor-pointer hover:bg-gray-50"
                            >
                              {user.role === 'admin' ? 'Revoke Admin Access' : 'Promote to Admin'}
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              onClick={() => updateUserRoleMutation.mutate({ userId: user.id, role: user.role === 'verified' ? 'user' : 'verified' })}
                              className="cursor-pointer hover:bg-gray-50"
                            >
                              {user.role === 'verified' ? 'Remove Verification' : 'Mark as Verified'}
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => banUserMutation.mutate({ userId: user.id, isBanned: user.isBanned })}
                              className={`cursor-pointer hover:bg-gray-50 ${user.isBanned ? 'text-green-600' : 'text-red-600'}`}
                            >
                              {user.isBanned ? (
                                <span className="flex items-center gap-2"><div className="w-3.5 h-3.5 border-2 border-green-600 rounded-full" /> Unban User</span>
                              ) : (
                                <span className="flex items-center gap-2"><XCircle size={14} /> Ban User</span>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen trading-bg relative">
      <TradingBackground variant="grid" />

      {/* ... Header and Stats ... */}
      <div className="trading-card trading-border relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center">
              <AnalyticsLineIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold trading-text">Admin Panel</h1>
              <p className="trading-text-muted">Manage users, content, and moderation</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {/* ... stats cards ... */}
          <Card className="trading-card shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <VolumeBarIcon className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold trading-text">{stats.totalUsers}</p>
                  <p className="text-xs trading-text-muted">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="trading-card shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ArrowUpIcon className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold trading-text">{stats.verifiedContributors}</p>
                  <p className="text-xs trading-text-muted">Verified</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="trading-card shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AnalyticsLineIcon className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="text-2xl font-bold trading-text">{stats.admins}</p>
                  <p className="text-xs trading-text-muted">Admins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="trading-card shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ChartGridIcon className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold trading-text">{stats.kbArticles}</p>
                  <p className="text-xs trading-text-muted">KB Articles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="trading-card shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CandlestickIcon className="w-8 h-8 text-rose-500" />
                <div>
                  <p className="text-2xl font-bold trading-text">{stats.messages}</p>
                  <p className="text-xs trading-text-muted">Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="users">
          <TabsList className="mb-6">
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="knowledge">Knowledge Base</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UserManagementContent />
          </TabsContent>

          <TabsContent value="knowledge" className="space-y-4">
            {/* FORCE REFRESH CONTAINER */}
            <div className="bg-white border text-gray-900 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Knowledge Base Management</h2>
                  <p className="text-gray-500 text-sm">Create and edit guides for your students.</p>
                </div>
                <div className="flex gap-3">
                  <Button onClick={seedKBData} variant="outline" className="border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
                    Populate Default Data
                  </Button>
                  <Button onClick={() => setIsAddingEntry(true)} className="bg-blue-600 text-white hover:bg-blue-700">
                    + Add New Article
                  </Button>
                </div>
              </div>

              <div className="p-6 bg-gray-50 min-h-[400px]">
                {/* Permission Error Alert */}
                {kbError && kbError.code === 'permission-denied' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
                    <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-sm">Database Access Denied</h3>
                      <p className="text-sm mt-1">
                        Firebase is blocking access. Please go to Firebase Console &gt; Firestore Database &gt; Rules and allow read/write access.
                        <br />
                        <span className="text-xs opacity-75">Showing dummy data below for preview purposes.</span>
                      </p>
                    </div>
                  </div>
                )}

                {loadingKB && displayEntries.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : displayEntries.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Pencil className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="text-gray-900 font-medium text-lg">No articles found</div>
                    <p className="text-gray-500 mb-6">Get started by populate data or add new one.</p>
                    <Button onClick={seedKBData} variant="outline" className="mx-auto border-blue-200 text-blue-700 hover:bg-blue-50">
                      Populate Default Data
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {displayEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all relative group"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 pr-12">
                            {/* Category Badge */}
                            <span className="inline-block px-3 py-1 rounded bg-slate-900 text-white text-xs font-bold uppercase tracking-wide mb-3">
                              {entry.category?.replace("_", " ")}
                            </span>

                            {/* Title */}
                            <h3 className="font-bold text-lg text-gray-900 mb-2 leading-tight">
                              {entry.title || entry.question}
                            </h3>

                            {/* Content Snippet */}
                            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed mb-4">
                              {entry.content || entry.answer}
                            </p>

                            {/* Metadata */}
                            <div className="flex items-center gap-4 text-xs text-gray-400 font-medium pt-4 border-t border-gray-50 mt-auto">
                              <span>By: {entry.contributor_name || "Admin"}</span>
                              <span>Views: {entry.views || 0}</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity absolute top-6 right-6">
                            <button
                              onClick={() => handleEditEntry(entry)}
                              className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all bg-white"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => deleteKBEntryMutation.mutate(entry.id)}
                              className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all bg-white"
                              title="Delete"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add New KB Entry Modal */}
      <Dialog open={isAddingEntry} onOpenChange={setIsAddingEntry}>
        <DialogContent className="sm:max-w-[500px] bg-white text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add New Knowledge Base Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select
                value={newEntryForm.category}
                onValueChange={(v) => setNewEntryForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  {PREDEFINED_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-gray-900 hover:bg-gray-100 focus:bg-gray-100">
                      {cat.replace("_", " ")}
                    </SelectItem>
                  ))}
                  <SelectItem value="other" className="text-blue-600 font-semibold hover:bg-gray-100 focus:bg-gray-100">
                    + Other (Custom)
                  </SelectItem>
                </SelectContent>
              </Select>
              {newEntryForm.category === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-1">
                  <Input
                    placeholder="Enter custom category name"
                    value={newEntryForm.customCategory}
                    onChange={(e) => setNewEntryForm(prev => ({ ...prev, customCategory: e.target.value }))}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Title</label>
              <Textarea
                placeholder="Article Title..."
                value={newEntryForm.title}
                onChange={(e) => setNewEntryForm(prev => ({ ...prev, title: e.target.value }))}
                rows={1}
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 resize-none font-semibold"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Content</label>
              <Textarea
                placeholder="The content of your article..."
                value={newEntryForm.content}
                onChange={(e) => setNewEntryForm(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddingEntry(false)} className="text-gray-700 border-gray-300">
              Cancel
            </Button>
            <Button onClick={handleAddEntry} disabled={addKBEntryMutation.isPending} className="bg-blue-600 text-white hover:bg-blue-700">
              {addKBEntryMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Add Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit KB Entry Modal */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="sm:max-w-[500px] bg-white text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Edit Knowledge Base Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select
                value={editForm.category}
                onValueChange={(v) => setEditForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  {PREDEFINED_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-gray-900 hover:bg-gray-100 focus:bg-gray-100">
                      {cat.replace("_", " ")}
                    </SelectItem>
                  ))}
                  <SelectItem value="other" className="text-blue-600 font-semibold hover:bg-gray-100 focus:bg-gray-100">
                    + Other (Custom)
                  </SelectItem>
                </SelectContent>
              </Select>
              {editForm.category === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-1">
                  <Input
                    placeholder="Enter custom category name"
                    value={editForm.customCategory}
                    onChange={(e) => setEditForm(prev => ({ ...prev, customCategory: e.target.value }))}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Title</label>
              <Textarea
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                rows={1}
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 resize-none font-semibold"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Content</label>
              <Textarea
                value={editForm.content}
                onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEntry(null)} className="text-gray-700 border-gray-300">
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} disabled={updateKBEntryMutation.isPending} className="bg-blue-600 text-white hover:bg-blue-700">
              {updateKBEntryMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
